import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { WORKSPACE, LOGOUT } from "@/constants/testIds";
import { LogOut, Grid3x3, Languages, ShieldCheck, Menu, Download } from "lucide-react";

export default function AppHeader({ onOpenSidebar }) {
  const { t, i18n } = useTranslation();
  const { user, logout, updateLanguage } = useAuth();

  const switchLang = () => {
    const next = i18n.language === "zh" ? "en" : "zh";
    i18n.changeLanguage(next);
    try { localStorage.setItem("lang", next); } catch { /* ignore */ }
    if (user) updateLanguage?.(next);
  };

  return (
    <header className="h-14 flex-shrink-0 flex items-center justify-between px-3 sm:px-5 border-b border-[hsl(var(--brand)/0.15)] bg-[hsl(var(--brand-tint))]/60 backdrop-blur-xl relative z-20">
      <div className="flex items-center gap-2 sm:gap-3 min-w-0">
        {onOpenSidebar && (
          <button
            data-testid="header-open-sidebar"
            onClick={onOpenSidebar}
            className="btn-swiss h-9 w-9 flex items-center justify-center flex-shrink-0"
            aria-label="Open files"
            title={t("files")}
          >
            <Menu className="w-4 h-4" strokeWidth={1.75} />
          </button>
        )}
        <div className="w-8 h-8 rounded-md border-2 border-[hsl(var(--brand))] flex items-center justify-center bg-white flex-shrink-0 shadow-[0_2px_8px_-2px_hsl(var(--brand)/0.35)]">
          <Grid3x3 className="w-4 h-4 text-[hsl(var(--brand))]" strokeWidth={2} />
        </div>
        <div className="min-w-0">
          <div className="font-display font-bold text-sm tracking-tight leading-none truncate">{t("app_name")}</div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground leading-none mt-1 hidden sm:block">
            {t("app_tagline")}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
        {user?.role === "admin" && (
          <Link
            to="/admin/users"
            data-testid="header-admin-link"
            className="btn-swiss h-9 px-2 sm:px-3 gap-1.5"
            title={t("admin_panel")}
          >
            <ShieldCheck className="w-4 h-4" strokeWidth={1.75} />
            <span className="hidden md:inline text-xs">{t("admin_panel")}</span>
          </Link>
        )}
        <Link
          to="/install"
          data-testid="header-install-link"
          className="btn-swiss h-9 px-2 sm:px-3 gap-1.5"
          title={t("install_title")}
        >
          <Download className="w-4 h-4" strokeWidth={1.75} />
          <span className="hidden md:inline text-xs">{t("install_short") || "安装"}</span>
        </Link>
        <button
          data-testid={WORKSPACE.headerLangToggle}
          onClick={switchLang}
          className="btn-swiss h-9 px-2 sm:px-3 text-xs gap-1.5"
          title={t("language")}
        >
          <Languages className="w-3.5 h-3.5" strokeWidth={1.5} />
          <span className="hidden xs:inline">{i18n.language === "zh" ? "中文 / EN" : "EN / 中文"}</span>
        </button>

        {user && (
          <div className="flex items-center gap-2 sm:gap-3 pl-2 sm:pl-3 ml-0.5 sm:ml-1 border-l border-border">
            <div className="text-right hidden lg:block">
              <div className="text-xs font-semibold leading-none">{user.name}</div>
              <div className="text-[10px] font-mono text-muted-foreground leading-none mt-1">{user.email}</div>
            </div>
            <div className="w-9 h-9 border border-foreground/70 flex items-center justify-center font-display font-bold text-sm uppercase flex-shrink-0">
              {(user.name || user.email || "?")[0]}
            </div>
            <button
              data-testid={WORKSPACE.headerLogout}
              onClick={logout}
              className="btn-swiss h-9 px-2 sm:px-3"
              title={t("logout")}
            >
              <LogOut className="w-4 h-4" strokeWidth={1.5} />
            </button>
          </div>
        )}
      </div>
    </header>
  );
}
